/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from '@/shared/ui/radix-icons';
import type { VisitStatus } from '../constants/visit-colors';
import { KanbanCard } from './KanbanCard';
import type { KanbanVisit } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';
import { Text } from '@/shared/ui';
import { useIsSoloBroker } from '@/shared/hooks/use-is-solo-broker';

/** The four visit-status columns, in display order, shared across all layouts. */
export const COLUMNS: VisitStatus[] = ['pending', 'confirmed', 'done', 'cancelled'];

/** A single card list for a status column, shared across desktop and mobile layouts. */
export const ColumnCards: React.FC<{
  cards: KanbanVisit[];
  selectedId: string | null;
  onSelectVisit: (id: string | null) => void;
  onDragStart?: (e: React.DragEvent, id: string) => void;
  onDragEnd?: () => void;
  /** Phone only: opens the move sheet for one visit. */
  onMoveStatus?: (visit: KanbanVisit) => void;
}> = ({ cards, selectedId, onSelectVisit, onDragStart, onDragEnd, onMoveStatus }) => {
  const { t } = useTranslation('features/visits');
  const isSoloBroker = useIsSoloBroker();
  if (cards.length === 0) {
    return <EmptyColumnState label={t('kanban.noVisits')} />;
  }
  return (
    <>
      {cards.map((v) => (
        <KanbanCard
          key={v.id}
          id={v.id}
          property={v.property}
          client={v.client}
          date={v.date}
          time={v.time}
          broker={isSoloBroker ? '' : v.broker}
          status={v.status}
          selected={v.id === selectedId}
          onClick={() => onSelectVisit(v.id === selectedId ? null : v.id)}
          onDragStart={onDragStart ? (e) => onDragStart(e, v.id) : undefined}
          onDragEnd={onDragEnd}
          onMoveStatus={onMoveStatus ? () => onMoveStatus(v) : undefined}
        />
      ))}
    </>
  );
};

/** Polished empty state for a Kanban column with no visits. */
const EmptyColumnState: React.FC<{ label: string }> = ({ label }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--space-2)',
      padding: 'var(--space-6) var(--space-4)',
      margin: 'var(--space-2)',
      borderRadius: 'var(--radius-md)',
      border: '1.5px dashed var(--border-subtle)',
      background: 'var(--surface-secondary)',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: 'color-mix(in srgb, var(--text-tertiary) 8%, transparent)',
      }}
    >
      <CalendarIcon
        width={18}
        height={18}
        style={{ color: 'var(--text-tertiary)', opacity: 0.6 } as React.CSSProperties}
      />
    </div>
    <Text
      style={{
        fontSize: 'var(--text-meta)',
        color: 'var(--text-tertiary)',
        fontWeight: 'var(--font-medium)' as unknown as number,
      }}
    >
      {label}
    </Text>
  </div>
);

/** Small rounded count badge tinted with the column accent. */
export const CountBadge: React.FC<{ accent: string; count: number }> = ({ accent, count }) => (
  <Text as="span" style={{ fontSize: 'var(--text-meta)', fontWeight: 'var(--font-bold)' as unknown as number, padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)', background: `${accent}28`, color: accent, flexShrink: 0 }}>
    {count}
  </Text>
);

/** Shared style for the carousel pager (prev/next) buttons. */
export const pagerBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 36, height: 36, borderRadius: 'var(--radius-md)', cursor: 'pointer',
  background: 'var(--surface-primary)', border: '1px solid var(--border-subtle)',
  color: 'var(--text-secondary)',
};
